/**
 * Окошко модели по наведению на карточку каталога: вращающаяся 3D-модель, название и
 * габариты. Встаёт сбоку от карточки — с той стороны, где больше места, — и не ловит мышь:
 * её можно вести дальше по карточкам. Сцена та же, что у «глазика» (`ModelPreviewScene`),
 * и грузится лениво: three.js не попадает в основной бандл.
 *
 * Рисуется в `body`: панели каталога обрезают всё, что выходит за их край.
 */
import { lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { UIKit } from "@/shared/ui";
import type { ModelPeekTarget } from "../hooks/useModelPeek";

const ModelPreviewScene = lazy(() => import("./ModelPreviewScene"));

/** Сторона окошка с моделью, px */
const PEEK_PX = 280;
/** Отступ от карточки и от краёв окна, px */
const GAP = 12;
/** Высота подписи под моделью, px — чтобы окошко целиком помещалось по высоте */
const CAPTION_PX = 56;

export const ModelPeek = ({ peek }: { peek: ModelPeekTarget }) => {
	const { item, title, anchor } = peek;
	// Справа от карточки, а если там не помещается — слева (у правой панели свойств)
	const fitsRight = anchor.right + GAP + PEEK_PX + GAP <= window.innerWidth;
	const left = fitsRight ? anchor.right + GAP : anchor.left - GAP - PEEK_PX;
	const maxTop = window.innerHeight - PEEK_PX - CAPTION_PX - GAP;
	const size = `${item.width} × ${item.depth}, высота ${item.height} мм`;

	return createPortal(
		<div
			aria-hidden="true"
			className="pointer-events-none fixed z-50 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
			style={{ left, top: Math.max(GAP, Math.min(anchor.top, maxTop)), width: PEEK_PX }}
		>
			<div style={{ width: PEEK_PX, height: PEEK_PX }}>
				<Suspense
					fallback={
						<div className="flex h-full items-center justify-center text-xs text-muted">
							Загрузка 3D…
						</div>
					}
				>
					<ModelPreviewScene view3d={item.view3d} />
				</Suspense>
			</div>
			<div className="flex flex-col gap-0.5 px-3 py-2">
				<UIKit.Text className="truncate text-sm font-semibold text-ink">{title}</UIKit.Text>
				<UIKit.Text className="text-xs text-muted">{size}</UIKit.Text>
			</div>
		</div>,
		document.body,
	);
};
